import Icon from '@atoms/Icon/Icon';
import { IconName } from '@models/IIcon';
import { ILink } from '@models/ILink';
import classNames from 'classnames';
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  PropsWithChildren,
} from 'react';

interface Props {
  readonly className?: string;
  readonly dataTestId?: string;
  readonly disabled?: boolean;
  readonly editHint?: string;
  readonly hasCustomStyling?: boolean;
  readonly link?: ILink;
  readonly linkCallback?: () => void;
  readonly onFocus?: () => void;
  readonly secondary?: boolean;
  readonly secondaryBackgroundClassName?: string;
  readonly icon?: IconName;
  readonly isLightMode?: boolean;
  readonly inverted?: boolean;
  readonly tabIndex?: number;
}

type ButtonProps = Props &
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

type AnchorProps = Props &
  DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>;

const Button = ({
  children,
  className,
  dataTestId,
  disabled = false,
  editHint,
  hasCustomStyling,
  link,
  linkCallback,
  onFocus,
  secondary = false,
  secondaryBackgroundClassName = 'bg-transparent',
  icon,
  isLightMode,
  inverted = false,
  tabIndex,
  ...rest
}: PropsWithChildren<ButtonProps | AnchorProps>) => {
  const buttonClasses = classNames(
    {
      'bg-yellow py-1 px-4 text-black-100 hover:bg-yellow-100 focus:bg-yellow-500 active:bg-yellow-500':
        !hasCustomStyling && !disabled && !secondary && !inverted,
      //
      [`${secondaryBackgroundClassName} border border-yellow py-[3px] px-[15px] text-black-100 hover:border-yellow-100 hover:bg-yellow-100 hover:text-black-100 focus:border-yellow-500 focus:bg-yellow-500 active:border-yellow-500 active:bg-yellow-500 ${
        isLightMode
          ? ''
          : 'dark:text-white dark:hover:text-black-100 dark:focus:text-black-100 dark:active:text-black-100'
      }`]: !hasCustomStyling && !disabled && secondary && !inverted,
      //
      'bg-grey text-grey-500 py-1 px-4': disabled,
      //
      'bg-black text-white py-1 px-4 active:bg-grey-600 hover:bg-grey-600 focus:bg-grey-600':
        !hasCustomStyling && !disabled && !secondary && inverted,
      //
      'flex items-center w-fit': icon,
    },
    'cta rounded-full inline-block min-h-min select-none',
    className
  );

  return link?.url && (link?.name || children) && !disabled ? (
    <a
      {...(rest as AnchorProps)}
      className={buttonClasses}
      href={link.url}
      target={link.target}
      data-epi-property-name={editHint}
      onClick={linkCallback}
      data-testid={dataTestId}
      onFocus={onFocus}
      tabIndex={tabIndex}
      {...(icon === 'download' && { download: link.url })}
    >
      {children || link.name}
      {icon && <Icon name={icon} className="ml-2" size="18px" />}
    </a>
  ) : (
    <button
      {...(rest as ButtonProps)}
      className={buttonClasses}
      disabled={disabled}
      data-epi-property-name={editHint}
      data-testid={dataTestId}
      onFocus={onFocus}
      tabIndex={tabIndex}
    >
      {children}
      {icon && <Icon name={icon} className="ml-2" size="18px" />}
    </button>
  );
};

export default Button;
